// Invoice create/edit — a full page (matches the Add/Edit pages for domains,
// servers, clients and users). One component handles both: a route param `id`
// means edit, no id means create. Totals are a live client-side preview; the
// server recomputes them authoritatively on save.
//
// Fields are grouped into Client Details / Invoice Details / Product Details
// (mirrors the client's reference layout). Tax is PER ITEM; the discount is
// applied after tax. Picking a saved client auto-fills the client snapshot.
import { useState, useEffect, useMemo, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  MenuItem,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { COLORS, SHADOW } from "../theme/tokens";
import { HEADING_FONT } from "../theme/fonts";
import api from "../api";
import { useCreateInvoice, useUpdateInvoice, useBankAccounts } from "../hooks/queries";
import ClientPicker from "../components/ClientPicker";

const STATUS_OPTIONS = ["Draft", "Sent", "Paid", "Overdue", "Cancelled"];
// Must match INVOICE_TYPES in backend/src/routes/invoices.js.
const INVOICE_TYPES = [
  "Tax Invoice (With GST)",
  "Invoice (Without GST)",
  "Proforma Invoice",
];
const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyItem = () => ({ description: "", qty: 1, rate: 0, tax: 18 });
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const inr = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function emptyForm() {
  return {
    // Deliberately blank — the user must pick a type ("Select Invoice Type").
    invoiceType: "",
    invoiceDate: todayISO(),
    dueDate: "",
    bankAccountId: "",
    clientId: "",
    clientName: "",
    companyName: "",
    clientCode: "",
    clientEmail: "",
    clientPhone: "",
    clientGstin: "",
    billingAddress: "",
    country: "India",
    state: "",
    location: "",
    sublocation: "",
    regDate: "",
    status: "Draft",
    discountType: "amount",
    discountValue: 0,
    notes: "",
    items: [emptyItem()],
  };
}

// Static design tokens + style objects. Hoisted to module scope so they keep a
// stable identity across renders — rebuilding them on every keystroke made
// Emotion re-serialize every field's `sx` and slowed typing.
const colors = {
  text: COLORS.ink,
  textMuted: COLORS.muted,
  textFaint: COLORS.faint,
  cardBg: COLORS.surface,
  cardBorder: COLORS.hairline,
  cardBorderHover: COLORS.borderHover,
  hover: COLORS.tile,
  divider: COLORS.hairline,
  glassBg: COLORS.surface,
  glassBorder: COLORS.hairline,
  shadowSm: SHADOW.card,
};

const labelSx = { display: "block", fontSize: 13, fontWeight: 600, color: colors.text, mb: 0.75 };
// Clean solid-white section card (no glass blur — it did nothing inside the
// white modal and only softened the edges). Crisp hairline border + subtle shadow.
const cardSx = {
  background: "#fff",
  border: `1px solid ${colors.cardBorder}`,
  borderRadius: 2.5,
  boxShadow: colors.shadowSm,
  p: 2.5,
};
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    background: colors.cardBg,
    color: colors.text,
    borderRadius: 2,
    "& fieldset": { borderColor: colors.cardBorder },
    "&:hover fieldset": { borderColor: colors.cardBorderHover },
    "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
  },
  "& input": { color: colors.text },
  "& textarea": { color: colors.text },
  "& .MuiSelect-select": { color: colors.text },
  "& .MuiSvgIcon-root.MuiSelect-icon": { color: colors.textMuted },
  "& input::placeholder, & textarea::placeholder": { color: colors.textFaint, opacity: 1 },
};
const gridSx = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 };
const grid3Sx = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 };
// Line-items grid: #, Item, Qty, Rate, Tax%, Amount, delete
const ITEM_COLS = "32px minmax(0, 1fr) 80px 100px 80px 120px 44px";

// Labeled TextField used across the form sections. MUST stay at module scope: if
// it were defined inside InvoiceForm, every keystroke would give it a new
// function identity and React would unmount/remount every field — the lag source.
function Field({ label, value, onChange, type, select, children, ...rest }) {
  return (
    <Box>
      <Typography component="label" sx={labelSx}>{label}</Typography>
      <TextField fullWidth size="small" type={type} select={select} value={value} onChange={onChange} sx={fieldSx} {...rest}>
        {children}
      </TextField>
    </Box>
  );
}

function SectionHeader({ icon, title, subtitle, colors }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          background:
            "linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.18) 100%)",
          color: "#3B82F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 700, color: colors.text, lineHeight: 1.2, mt: "2px" }}>
          {title}
        </Typography>
        {subtitle && <Typography sx={{ fontSize: 12, color: colors.textMuted }}>{subtitle}</Typography>}
      </Box>
    </Stack>
  );
}

// Renders as a full page by default (routes /add-invoice, /invoices/:id/edit).
// Pass `embedded` (used by AddInvoiceModal) to drop the page chrome (layout /
// back / hero) and report success/cancel via callbacks instead of navigating.
function InvoiceForm({ embedded = false, invoiceId = null, onSuccess, onCancel } = {}) {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = invoiceId ?? routeId;
  const isNew = !id;

  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();
  // RABS bank accounts — the "Account" dropdown (which account this invoice is
  // to be paid into; it prints in the PDF's payment footer).
  const { data: bankAccounts = [] } = useBankAccounts();

  const [form, setForm] = useState(emptyForm());
  const [invoiceNo, setInvoiceNo] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load the invoice on edit.
  useEffect(() => {
    if (isNew) return;
    let active = true;
    api
      .get(`/invoices/${id}`)
      .then((inv) => {
        if (!active) return;
        setInvoiceNo(inv.invoiceNo || "");
        setForm({
          invoiceType: INVOICE_TYPES.includes(inv.invoiceType) ? inv.invoiceType : "",
          invoiceDate: inv.invoiceDate ? String(inv.invoiceDate).slice(0, 10) : todayISO(),
          dueDate: inv.dueDate ? String(inv.dueDate).slice(0, 10) : "",
          bankAccountId: inv.bankAccountId ?? "",
          clientId: inv.clientId ?? "",
          clientName: inv.clientName || "",
          companyName: inv.companyName || "",
          clientCode: inv.clientCode || "",
          clientEmail: inv.clientEmail || "",
          clientPhone: inv.clientPhone || "",
          clientGstin: inv.clientGstin || "",
          billingAddress: inv.billingAddress || "",
          country: inv.country || "India",
          state: inv.state || "",
          location: inv.location || "",
          sublocation: inv.sublocation || "",
          regDate: inv.regDate ? String(inv.regDate).slice(0, 10) : "",
          status: inv.status || "Draft",
          discountType: inv.discountType || "amount",
          discountValue: inv.discountValue ?? 0,
          notes: inv.notes || "",
          items: inv.items?.length
            ? inv.items.map((it) => ({
                description: it.description,
                qty: it.qty,
                rate: it.rate,
                tax: it.tax ?? 18,
              }))
            : [emptyItem()],
        });
      })
      .catch((err) => {
        if (active) setLoadError(err.message || "Failed to load invoice");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, isNew]);

  // Live preview totals (server is authoritative on save). Per-item tax, then
  // discount on the taxed gross. The tax always splits as CGST + SGST — there is
  // no GST-type field anymore, so the payload never asks for the IGST split.
  const totals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;
    form.items.forEach((it) => {
      const amount = round2((Number(it.qty) || 0) * (Number(it.rate) || 0));
      subtotal += amount;
      totalTax += round2((amount * (Number(it.tax) || 0)) / 100);
    });
    subtotal = round2(subtotal);
    totalTax = round2(totalTax);
    const cgst = round2(totalTax / 2);
    const sgst = round2(totalTax - cgst);
    const gross = round2(subtotal + totalTax);
    const dVal = Number(form.discountValue) || 0;
    let discountAmount = form.discountType === "percent" ? round2((gross * dVal) / 100) : round2(dVal);
    if (discountAmount < 0) discountAmount = 0;
    if (discountAmount > gross) discountAmount = gross;
    return { subtotal, cgst, sgst, discountAmount, total: round2(gross - discountAmount) };
  }, [form.items, form.discountType, form.discountValue]);

  // --- Line item handlers ---
  function updateItem(idx, key, value) {
    setForm((f) => {
      const items = f.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
      return { ...f, items };
    });
  }
  function addItem() {
    setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  }
  function removeItem(idx) {
    setForm((f) => ({
      ...f,
      items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
    }));
  }

  // Validate + shape the form into an API payload. Returns null (and sets the
  // error) when the form is incomplete.
  function buildPayload() {
    if (!form.invoiceType) {
      setSubmitError("Please select an invoice type");
      return null;
    }
    const clientName = form.clientName.trim();
    if (!clientName) {
      setSubmitError("Client name is required");
      return null;
    }
    const validItems = form.items.filter((it) => (it.description || "").trim());
    if (!validItems.length) {
      setSubmitError("Add at least one line item with a description");
      return null;
    }
    return {
      ...form,
      clientName,
      discountValue: Number(form.discountValue) || 0,
      items: validItems.map((it) => ({
        description: it.description.trim(),
        qty: Number(it.qty) || 0,
        rate: Number(it.rate) || 0,
        tax: Number(it.tax) || 0,
      })),
    };
  }

  // Save the invoice (create or update) and return { id, invoiceNo }.
  async function persistInvoice(payload) {
    if (isNew) {
      const created = await createInvoice.mutateAsync(payload);
      return { id: created?.id, invoiceNo: created?.invoiceNo };
    }
    await updateInvoice.mutateAsync({ id, data: payload });
    return { id, invoiceNo };
  }

  // Fetch the invoice PDF with the auth token and trigger a browser download.
  // (api.js only handles JSON, so we fetch the blob directly.)
  async function downloadInvoicePdf(invId, invoiceNoStr) {
    const token = localStorage.getItem("authToken") || "";
    const res = await fetch(`${api.baseUrl}/api/invoices/${invId}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to generate PDF");
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    
    a.href = url;
    a.download = `Invoice-${String(invoiceNoStr || invId).replace(/[^\w.-]+/g, "_")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  function finalize() {
    if (embedded) {
      onSuccess?.({ isEdit: !isNew });
    } else {
      setSnackbarOpen(true);
      setTimeout(() => navigate("/invoices"), 700);
    }
  }

  async function handleSubmit(e) {
    if (e?.preventDefault) e.preventDefault();
    setSubmitError("");
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    try {
      await persistInvoice(payload);
      finalize();
    } catch (err) {
      setSubmitError(err.message || "Failed to save invoice");
      setSaving(false);
    }
  }

  // Print = save the invoice first (so the PDF reflects the latest data), then
  // download it. Closes/finalizes afterwards like a normal save so a brand-new
  // invoice can't be created twice.
  async function handlePrint() {
    setSubmitError("");
    const payload = buildPayload();
    if (!payload) return;
    setPrinting(true);
    try {
      const { id: invId, invoiceNo: invNo } = await persistInvoice(payload);
      if (!invId) throw new Error("Could not resolve the saved invoice to print");
      await downloadInvoicePdf(invId, invNo);
      finalize();
    } catch (err) {
      setSubmitError(err.message || "Failed to print invoice");
      setPrinting(false);
    }
  }

  const Wrapper = embedded ? Fragment : DashboardLayout;
  return (
    <Wrapper>
      {!embedded && (
        <>
      <Button
        onClick={() => navigate("/invoices")}
        startIcon={<ArrowBackIcon />}
        sx={{ textTransform: "none", fontWeight: 600, color: colors.textMuted, mb: 2, ml: -1, "&:hover": { background: colors.hover, color: colors.text } }}
      >
        Back to Invoices
      </Button>

      {/* Hero card */}
      <Box sx={{ position: "relative", background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)", borderRadius: 3, p: { xs: 3, md: 4 }, mb: 3, overflow: "hidden", color: "#fff" }}>
        <Box sx={{ position: "absolute", top: -60, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.12)", filter: "blur(40px)" }} />
        <Stack direction="row" spacing={2} alignItems="center" sx={{ position: "relative", zIndex: 1, minWidth: 0 }}>
          <Box sx={{ width: 60, height: 60, borderRadius: 2, background: "rgba(255,255,255,0.18)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: "1.5px", textTransform: "uppercase", opacity: 0.85 }}>
              {isNew ? "New Invoice" : "Edit Invoice"}
            </Typography>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: { xs: 24, md: 32 }, fontWeight: 700, letterSpacing: "-0.5px", lineHeight: 1.1, wordBreak: "break-word" }}>
              {isNew ? "New Invoice" : invoiceNo || form.clientName || "Invoice"}
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.5 }}>
              {isNew ? "Create a GST tax invoice" : "Update this invoice's details"}
            </Typography>
          </Box>
        </Stack>
      </Box>
        </>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#3B82F6" }} />
        </Box>
      ) : loadError ? (
        <Box sx={cardSx}>
          <Typography sx={{ color: colors.text }}>{loadError}</Typography>
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {/* ① Invoice Details */}
          <Box sx={cardSx}>
            <SectionHeader icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />} title="Invoice Details" subtitle="Type, dates, account and GST number" colors={colors} />
            <Divider sx={{ my: 1.75, borderColor: colors.divider }} />
            <Stack spacing={2.5}>
              <Box sx={grid3Sx}>
                <Field
                  label="Invoice Type *"
                  select
                  value={form.invoiceType}
                  onChange={(e) => setField("invoiceType", e.target.value)}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    <span style={{ color: colors.textFaint }}>Select Invoice Type</span>
                  </MenuItem>
                  {INVOICE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Field>
                <Field label="Invoice Date *" type="date" value={form.invoiceDate} onChange={(e) => setField("invoiceDate", e.target.value)} InputLabelProps={{ shrink: true }} />
                <Field label="Due Date" type="date" value={form.dueDate} onChange={(e) => setField("dueDate", e.target.value)} InputLabelProps={{ shrink: true }} />
              </Box>
              <Box sx={grid3Sx}>
                <Field
                  label="Account *"
                  select
                  value={form.bankAccountId}
                  onChange={(e) => setField("bankAccountId", e.target.value)}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">
                    <span style={{ color: colors.textFaint }}>Select Account</span>
                  </MenuItem>
                  {bankAccounts.map((ba) => (
                    <MenuItem key={ba.id} value={ba.id}>
                      {[ba.accountName, ba.bank].filter(Boolean).join(" - ")}
                    </MenuItem>
                  ))}
                </Field>
                <Field label="GST No." value={form.clientGstin} onChange={(e) => setField("clientGstin", e.target.value)} placeholder="Enter GST No." />
                <Field label="Status" select value={form.status} onChange={(e) => setField("status", e.target.value)}>
                  {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Field>
              </Box>
            </Stack>
          </Box>

          {/* ② Client Details */}
          <Box sx={cardSx}>
            <SectionHeader icon={<PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />} title="Client Details" subtitle="Pick a client to auto-fill, or type manually" colors={colors} />
            <Divider sx={{ my: 1.75, borderColor: colors.divider }} />
            <Stack spacing={2.5}>
              <Box>
                <Typography component="label" sx={labelSx}>Link Client (auto-fills the fields below)</Typography>
                <ClientPicker
                  value={form.clientId}
                  onChange={(cid) => setField("clientId", cid || "")}
                  onSelect={(client) => {
                    if (!client) return;
                    setForm((f) => ({
                      ...f,
                      clientId: client.id,
                      clientName: client.name || f.clientName,
                      companyName: client.companyName || f.companyName,
                      clientCode: client.clientCode || f.clientCode,
                      clientEmail: client.email || f.clientEmail,
                      clientPhone: client.phone || f.clientPhone,
                      clientGstin: client.gstin || f.clientGstin,
                      billingAddress: client.billingAddress || f.billingAddress,
                      country: client.country || f.country,
                      state: client.state || f.state,
                      location: client.locationName || f.location,
                      sublocation: client.sublocationName || f.sublocation,
                      regDate: client.regDate ? String(client.regDate).slice(0, 10) : f.regDate,
                    }));
                  }}
                  colors={colors}
                  fieldSx={fieldSx}
                  placeholder="Select a saved client…"
                />
              </Box>
              <Box sx={gridSx}>
                <Field label="Client Code *" value={form.clientCode} onChange={(e) => setField("clientCode", e.target.value)} />
                <Field label="Company *" value={form.companyName} onChange={(e) => setField("companyName", e.target.value)} />
              </Box>
              <Box sx={gridSx}>
                <Field label="Name *" value={form.clientName} onChange={(e) => setField("clientName", e.target.value)} />
                <Field label="Contact No. *" value={form.clientPhone} onChange={(e) => setField("clientPhone", e.target.value)} />
              </Box>
              <Box sx={gridSx}>
                <Field label="Email *" value={form.clientEmail} onChange={(e) => setField("clientEmail", e.target.value)} />
                <Field label="Country *" value={form.country} onChange={(e) => setField("country", e.target.value)} />
              </Box>
              <Box sx={grid3Sx}>
                <Field label="State" value={form.state} onChange={(e) => setField("state", e.target.value)} />
                <Field label="Location" value={form.location} onChange={(e) => setField("location", e.target.value)} />
                <Field label="Sublocation" value={form.sublocation} onChange={(e) => setField("sublocation", e.target.value)} />
              </Box>
              <Box sx={gridSx}>
                <Field label="Reg. Date" type="date" value={form.regDate} onChange={(e) => setField("regDate", e.target.value)} InputLabelProps={{ shrink: true }} />
                <Field label="Billing Address *" value={form.billingAddress} onChange={(e) => setField("billingAddress", e.target.value)} />
              </Box>
            </Stack>
          </Box>

          {/* ③ Product Details */}
          <Box sx={cardSx}>
            <SectionHeader icon={<FormatListBulletedOutlinedIcon sx={{ fontSize: 20 }} />} title="Product Details" subtitle="Line items, per-item tax and discount" colors={colors} />
            <Divider sx={{ my: 1.75, borderColor: colors.divider }} />

            {/* Line items — bordered, table-like container for breathing room */}
            <Box
              sx={{
                borderRadius: 2,
                border: `1px solid ${colors.cardBorder}`,
                background: colors.cardBg,
                overflow: "hidden",
              }}
            >
              {/* Header (desktop) */}
              <Box
                sx={{
                  display: { xs: "none", md: "grid" },
                  gridTemplateColumns: ITEM_COLS,
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  background: colors.hover,
                  borderBottom: `1px solid ${colors.divider}`,
                }}
              >
                {[
                  { label: "#", align: "center" },
                  { label: "Item Name", align: "left", pad: { pl: "14px" } },
                  { label: "Qty", align: "center" },
                  { label: "Rate", align: "center" },
                  { label: "Tax %", align: "center" },
                  { label: "Amount", align: "right", pad: { pr: "4px" } },
                  { label: "", align: "left" },
                ].map((h, i) => (
                  <Typography
                    key={i}
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: colors.textMuted,
                      textAlign: h.align,
                      ...(h.pad || {}),
                    }}
                  >
                    {h.label}
                  </Typography>
                ))}
              </Box>

              {form.items.map((it, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: ITEM_COLS },
                    gap: 1.5,
                    alignItems: "center",
                    px: { xs: 1.5, md: 2 },
                    py: 1.5,
                    borderBottom:
                      idx < form.items.length - 1 ? `1px solid ${colors.divider}` : "none",
                    transition: "background 0.15s ease",
                    "&:hover": { background: colors.hover },
                  }}
                >
                  <Typography sx={{ display: { xs: "none", md: "block" }, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>{idx + 1}</Typography>
                  <TextField size="small" placeholder="Item name / description" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} sx={fieldSx} />
                  <TextField size="small" type="number" placeholder="Qty" value={it.qty} onChange={(e) => updateItem(idx, "qty", e.target.value)} sx={fieldSx} inputProps={{ style: { textAlign: "right" } }} />
                  <TextField size="small" type="number" placeholder="Rate" value={it.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} sx={fieldSx} inputProps={{ style: { textAlign: "right" } }} />
                  <TextField size="small" type="number" placeholder="Tax %" value={it.tax} onChange={(e) => updateItem(idx, "tax", e.target.value)} sx={fieldSx} inputProps={{ style: { textAlign: "right" } }} />
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: colors.text, textAlign: "right", pr: 0.5, fontVariantNumeric: "tabular-nums" }}>
                    ₹{inr((Number(it.qty) || 0) * (Number(it.rate) || 0))}
                  </Typography>
                  <Box sx={{ textAlign: "center" }}>
                    <IconButton size="small" onClick={() => removeItem(idx)} disabled={form.items.length === 1} sx={{ color: colors.textMuted, "&:hover": { color: "#ef4444", background: "rgba(239,68,68,0.1)" } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>

            <Button startIcon={<AddIcon />} onClick={addItem} sx={{ textTransform: "none", fontWeight: 600, color: "#3B82F6", mt: 2, "&:hover": { background: "rgba(129,140,248,0.1)" } }}>
              Add Item
            </Button>

            <Divider sx={{ my: 2.5, borderColor: colors.divider }} />

            {/* Discount + totals — grouped on the right so there's no big empty
                gap between them; the slack sits under the items table on the left. */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} justifyContent="flex-end" alignItems="flex-start" sx={{ width: "100%" }}>
              <Box sx={{ width: { xs: "100%", sm: 260 }, flexShrink: 0 }}>
                <Typography component="label" sx={labelSx}>Discount</Typography>
                <Stack direction="row" spacing={1}>
                  <TextField select size="small" value={form.discountType} onChange={(e) => setField("discountType", e.target.value)} sx={{ ...fieldSx, width: 110 }}>
                    <MenuItem value="amount">₹</MenuItem>
                    <MenuItem value="percent">%</MenuItem>
                  </TextField>
                  <TextField size="small" type="number" placeholder="Enter Discount" value={form.discountValue} onChange={(e) => setField("discountValue", e.target.value)} sx={fieldSx} fullWidth />
                </Stack>
              </Box>

              {/* Right-side totals summary — plain lines (no boxed panel), grand
                  Total emphasised below a divider. */}
              <Box
                sx={{
                  width: { xs: "100%", sm: 300 },
                  alignSelf: { xs: "stretch", sm: "flex-start" },
                }}
              >
                {[
                  ["Subtotal", totals.subtotal],
                  ["CGST", totals.cgst],
                  ["SGST", totals.sgst],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 3, mb: 1.25 }}
                  >
                    <Typography sx={{ fontSize: 13, color: colors.textMuted }}>{label}</Typography>
                    <Typography sx={{ minWidth: 96, textAlign: "right", fontSize: 14, fontWeight: 600, color: colors.text, fontVariantNumeric: "tabular-nums" }}>
                      ₹{inr(value)}
                    </Typography>
                  </Box>
                ))}
                {totals.discountAmount > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 3, mb: 1.25 }}>
                    <Typography sx={{ fontSize: 13, color: "#ef4444" }}>Discount</Typography>
                    <Typography sx={{ minWidth: 96, textAlign: "right", fontSize: 14, fontWeight: 600, color: "#ef4444", fontVariantNumeric: "tabular-nums" }}>
                      − ₹{inr(totals.discountAmount)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1.5, borderColor: colors.divider }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 3 }}>
                  <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 16, fontWeight: 700, color: colors.text }}>Total</Typography>
                  <Typography sx={{ minWidth: 96, textAlign: "right", fontFamily: HEADING_FONT, fontSize: 20, fontWeight: 800, color: "#3B82F6", fontVariantNumeric: "tabular-nums" }}>
                    ₹{inr(totals.total)}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Notes */}
          <Box sx={cardSx}>
            <SectionHeader icon={<NotesOutlinedIcon sx={{ fontSize: 20 }} />} title="Notes" subtitle="Anything to print on the invoice" colors={colors} />
            <Divider sx={{ my: 1.75, borderColor: colors.divider }} />
            <TextField fullWidth size="small" multiline minRows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} sx={fieldSx} />
          </Box>

          {/* Actions — sticky bottom bar inside the modal so the buttons stay
              reachable while the form scrolls. Negative margins break out of the
              modal body padding so the bar spans the full width. */}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            sx={{
              pt: 2,
              borderTop: `1px solid ${colors.divider}`,
              ...(embedded && {
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                background: "#fff",
                mx: { xs: -2, md: -3 },
                px: { xs: 2, md: 3 },
                mb: { xs: -2, md: -3 },
                pb: 2,
                boxShadow: "0 -6px 16px rgba(16,24,40,0.06)",
              }),
            }}
          >
            <Button onClick={() => (embedded ? onCancel?.() : navigate("/invoices"))} sx={{ textTransform: "none", color: colors.textMuted, px: 3, py: 1.25, borderRadius: 2, "&:hover": { background: colors.hover, color: colors.text } }}>
              Cancel
            </Button>
            <Button
              onClick={handlePrint}
              disabled={saving || printing || !form.clientName.trim()}
              startIcon={<PrintOutlinedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 3,
                py: 1.25,
                borderRadius: 2,
                color: "#3B82F6",
                background: colors.cardBg,
                border: `1px solid ${colors.cardBorder}`,
                "&:hover": { background: colors.hover, borderColor: colors.cardBorderHover },
                "&.Mui-disabled": { color: colors.textFaint, borderColor: colors.cardBorder },
                transition: "all 0.15s ease",
              }}
            >
              {printing ? "Preparing…" : "Print"}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || printing || !form.clientName.trim()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 3.5,
                py: 1.25,
                borderRadius: 2,
                color: "#fff",
                background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
                "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)", transform: "translateY(-1px)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.6)", background: colors.cardBorder },
                transition: "all 0.15s ease",
              }}
            >
              {saving ? "Saving…" : isNew ? "Add Invoice" : "Save Changes"}
            </Button>
          </Stack>
        </Stack>
      )}

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity="success" variant="filled">Invoice saved successfully</Alert>
      </Snackbar>
      <Snackbar open={Boolean(submitError)} autoHideDuration={5000} onClose={() => setSubmitError("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity="error" variant="filled">{submitError}</Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default InvoiceForm;
